import * as Yup from 'yup';
import { isNil, isNumber } from 'lodash';

import { OvertimeEnum } from 'api/backofficeApiClient';
import {
  FinancialSchedulerFormValues,
  MultiRateFormScheduleValues,
} from 'components/modals/FinancialInfoScheduleModal';

const requiredMessage = 'Field is required';
const maxCharactersLength = 255;

const getMultiRateInitialValidationSchema = (
  isViewDTBillRateDtPayRateFields: boolean,
  isTeammate?: boolean,
) => {
  const schema: any = {
    billRate: Yup.number()
      .nullable()
      .min(0, 'Bill rate should be from 0 to 999999')
      .max(999999, 'Bill rate should be from 0 to 999999'),
    payRate: Yup.number()
      .nullable()
      .min(0, 'Pay rate should be from 0 to 999999')
      .max(999999, 'Pay rate should be from 0 to 999999')
      .when('billRate', {
        is: (billRate: number | null) => isNumber(billRate),
        then: Yup.number()
          .nullable()
          .max(Yup.ref('billRate'), 'Pay rate should be less than bill rate'),
      }),
    overtimeBillRate: Yup.number()
      .nullable()
      .min(0, 'OT bill rate should be from 0 to 999999')
      .max(999999, 'OT bill rate should be from 0 to 999999'),
    overtimePayRate: Yup.number()
      .nullable()
      .min(0, 'OT pay rate should be from 0 to 999999')
      .when('overtime', {
        is: (overtime: OvertimeEnum | null) =>
          overtime === OvertimeEnum.Nonexempt,
        then: Yup.number()
          .nullable()
          .max(999999, 'OT pay rate should be from 0 to 999999'),
      }),
    profitRate: Yup.number().nullable(),
    overtime: Yup.number().nullable(),
    multiRates: Yup.array().of(
      Yup.object(initialMultipleRatesValidationSchema),
    ),
  };

  if (isViewDTBillRateDtPayRateFields) {
    schema.dtBillRate = Yup.number()
      .nullable()
      .min(0, 'DT Bill rate should be from 0 to 999999')
      .max(999999, 'DT Bill rate should be from 0 to 999999');
    schema.dtPayRate = Yup.number().nullable();
  }

  if (!isTeammate) {
    schema.profitRate = Yup.number()
      .nullable()
      .min(0, 'Profit rate should be from 0 to 999999')
      .max(999999, 'Profit rate should be from 0 to 999999');
  }

  return schema;
};

const getSubmitValidationSchema = (
  isW2Assignment: boolean,
  isViewDTBillRateDtPayRateFields: boolean,
  predefinedFinancialSchedulerValues?: FinancialSchedulerFormValues,
  isTeammate?: boolean,
) => {
  const initialValues = getMultiRateInitialValidationSchema(
    isViewDTBillRateDtPayRateFields,
    isTeammate,
  );
  const schema: any = {
    ...initialValues,
    billRate: initialValues.billRate.required('Field is required'),
    payRate: initialValues.payRate.required('Field is required'),
    overtimeBillRate: Yup.lazy(() => {
      let schema: any = initialValues.overtimeBillRate;

      if (
        !predefinedFinancialSchedulerValues?.financialInfoScheduler?.hasOwnProperty(
          'overtimeBillRate',
        )
      ) {
        schema = initialValues.overtimeBillRate.when('overtime', {
          is: (overtime: OvertimeEnum | null) =>
            overtime !== OvertimeEnum.Exempt,
          then: initialValues.overtimeBillRate.required('Field is required'),
        });
      }

      return schema;
    }),
    overtimePayRate: initialValues.overtimePayRate.when('overtime', {
      is: (overtime: OvertimeEnum | null) => overtime !== OvertimeEnum.Exempt,
      then: initialValues.overtimePayRate.required('Field is required'),
    }),
    overtime: initialValues.overtime.required('Field is required'),
    multiRates: Yup.array().of(
      submitMultiRateValidationSchema(
        predefinedFinancialSchedulerValues?.multiRates,
      ),
    ),
  };

  if (isViewDTBillRateDtPayRateFields) {
    schema.dtBillRate = initialValues.dtBillRate.required('Field is required');
  }
  if (isW2Assignment) {
    schema.payType = Yup.number().nullable().required('Field is required');
  }

  return Yup.object(schema);
};

const initialPayCodeValidationSchema = {
  payCodeId: Yup.string(),
  payRate: Yup.number()
    .nullable()
    .min(0, 'Pay rate should be from 0 to 999999')
    .max(999999, 'Pay rate should be from 0 to 999999'),
  billRate: Yup.number()
    .nullable()
    .min(0, 'Bill rate should be from 0 to 999999')
    .max(999999, 'Bill rate should be from 0 to 999999'),
  specialInstructions: Yup.string()
    .trim()
    .max(
      maxCharactersLength,
      `Special instructions should be ${maxCharactersLength} characters maximum`,
    ),
  memberBilledIndicator: Yup.boolean().nullable(),
};

const initialMultipleRatesValidationSchema = {
  activePayCodeId: Yup.string(),
  payTypeId: Yup.string().nullable().required(requiredMessage),
  payCodesInfo: Yup.array().of(Yup.object(initialPayCodeValidationSchema)),
};

const submitPayCodeValidationSchema = () => {
  return Yup.object().shape(
    {
      ...initialPayCodeValidationSchema,
      memberBilledIndicator:
        initialPayCodeValidationSchema.memberBilledIndicator.when(
          ['billRate', 'payRate'],
          {
            is: (billRate: number, payRate: number) =>
              !isNil(billRate) || !isNil(payRate),
            then: initialPayCodeValidationSchema.memberBilledIndicator.required(
              requiredMessage,
            ),
          },
        ),
      payRate: initialPayCodeValidationSchema.payRate.when('billRate', {
        is: (billRate: number) => !isNil(billRate),
        then: initialPayCodeValidationSchema.payRate.required(requiredMessage),
      }),
      billRate: initialPayCodeValidationSchema.billRate.when('payRate', {
        is: (payRate: number) => !isNil(payRate),
        then: initialPayCodeValidationSchema.payRate.required(requiredMessage),
      }),
    },
    [['billRate', 'payRate']],
  );
};

const submitMultiRateValidationSchema = (
  predefinedMultiRatesScheduleValues?: MultiRateFormScheduleValues[],
) => {
  return Yup.object().shape({
    ...initialMultipleRatesValidationSchema,
    payCodesInfo: Yup.array()
      .of(submitPayCodeValidationSchema())
      .when('payTypeId', {
        is: (payTypeId: string) => {
          const matchingRateSchedule = predefinedMultiRatesScheduleValues?.find(
            (multiRate) => multiRate.payTypeId === payTypeId,
          );

          return !Boolean(matchingRateSchedule);
        },
        then: Yup.array().test(
          'at-least-one-full',
          'At least one pay code should be filled in',
          (array) => {
            return (array || []).some(
              (obj) => !isNil(obj.payRate) && !isNil(obj.billRate),
            );
          },
        ),
      }),
  });
};

export { getSubmitValidationSchema };
